import React from 'react';

import {asCoreObject} from 'app-core/framework/CoreObjectIntrospectionUtils';
import CoreObjectStarIconRenderer from 'app-core/platform/starrable/CoreObjectStarIcon';
import isArchived from 'archive/isArchived';
import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import {PublishingStatus, SubtemplateNode, TemplateNode} from 'graphql-client/__generated/graphqlGlobalTypes';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import ModifiedAtIcon from 'notebook/entry-templates/SelectTemplateCollectionItemTree/ModifiedAtIcon';
import OwnerAvatar from 'notebook/entry-templates/SelectTemplateCollectionItemTree/OwnerAvatar';
import {
  SelectTemplateCollectionItem,
  TemplateCollectionWithChildrenQuery,
} from 'notebook/entry-templates/SelectTemplateCollectionItemTree/types';
import {TreeItem} from 'ui-library/select-tree/SelectTreeConstants';
import {apiIdToIconName} from 'util/APIUtil';

export const FAVORITE_FOLDER_ID = 'tmplcol_favorite_folder';

interface CollectionsToSelectTreeItemsArgs {
  data: TemplateCollectionWithChildrenQuery;
  filterTemplateIds?: Array<string>;
  showUnpublishedCollectibles?: boolean;
}

const mapItemToTreeItem = (
  item: SelectTemplateCollectionItem,
  parentId: string | null,
  isStarred?: boolean
): TreeItem => ({
  id: item.id,
  icon: apiIdToIconName(item.id),
  name: item.name,
  parentId,
  secondaryLabels: [
    <CoreObjectStarIconRenderer item={asCoreObject(item)} isStarred={Boolean(isStarred)} />,
    <OwnerAvatar item={item} />,
    <ModifiedAtIcon item={item} />,
  ],
});

function shouldShowItem(
  item: TemplateNode | SubtemplateNode,
  filterTemplateIds: Array<string>,
  // Even when publishing enabled, we may want to show unpublished collectibles in the tree
  showUnpublishedCollectibles: boolean = false
): boolean {
  // Never show the item if we're filtering and it's not one of the items
  if (!isOneOfFilteredTemplates(item, filterTemplateIds)) {
    return false;
  }

  // If template publishing is disabled, we always show the template
  if (!BenchlingConfig.ELN_ENABLE_TEMPLATE_PUBLISHING || showUnpublishedCollectibles) {
    return true;
  }

  const currentPublishingRecord = item.currentPublishingRecord;
  if (currentPublishingRecord) {
    assert(!isInaccessible(currentPublishingRecord), 'Expected item to be accessible.');
    return currentPublishingRecord.publishingStatus === PublishingStatus.EFFECTIVE;
  }
  return false;
}

function isOneOfFilteredTemplates(
  item: TemplateNode | SubtemplateNode,
  filterTemplateIds: Array<string>
): boolean {
  if (filterTemplateIds.length === 0) {
    return true;
  }
  return filterTemplateIds.includes(item.id);
}

export function collectionsToSelectTreeItems({
  data,
  filterTemplateIds = [],
  // Even when publishing enabled, we may want to show unpublished collectibles in the tree
  showUnpublishedCollectibles = false,
}: CollectionsToSelectTreeItemsArgs): Array<TreeItem> {
  const favNode: TreeItem = {
    id: FAVORITE_FOLDER_ID,
    icon: 'star',
    name: 'Favorites',
    parentId: null,
  };

  const favorites: Array<TemplateNode | SubtemplateNode> = [];
  data.templateCollections.forEach((collection) => {
    const children = collection.children as unknown as Array<TemplateNode | SubtemplateNode>;
    favorites.push(...children.filter((item) => item.star?.isStarred));
  });

  const favTemplateItems = favorites
    .filter(
      (item) => !isArchived(item) && shouldShowItem(item, filterTemplateIds, showUnpublishedCollectibles)
    )
    .map((item) => mapItemToTreeItem(item, FAVORITE_FOLDER_ID, true))
    .sort((a, b) => a.name.localeCompare(b.name));

  const res: Array<TreeItem> = favTemplateItems.length > 0 ? [favNode, ...favTemplateItems] : [];

  const favTemplateIds = new Set(favTemplateItems.map((item) => item.id));

  data.templateCollections.forEach((collection) => {
    const children = (collection.children as unknown as Array<TemplateNode | SubtemplateNode>).filter(
      (item) => !isArchived(item) && shouldShowItem(item, filterTemplateIds, showUnpublishedCollectibles)
    );
    if (!isArchived(collection) && children.length > 0) {
      const isCollectionStarred = collection.star?.isStarred || false;
      res.push(mapItemToTreeItem(collection, null, isCollectionStarred));
    } else {
      // If the collection is archived, we don't want process its children
      return;
    }

    const childTreeItems = children
      .map((item) => mapItemToTreeItem(item, collection.id, favTemplateIds.has(item.id)))
      .sort((a, b) => a.name.localeCompare(b.name));
    res.push(...childTreeItems);
  });

  return res;
}
